import React, { useEffect, useState } from 'react'
import { Image, Linking, RefreshControl } from 'react-native'

import { publishEvent } from '@zet/analytics'
import Footer from '@zet/common/components/Footer'
import TransparentHeader from '@zet/common/components/header/transparentHeader'
import { StatusCard } from '@zet/common/components/statusCard'
import TotalTransactionBottomSheet from '@zet/common/components/statusCard/totalTransactionBottomSheet'
import Tag from '@zet/common/components/tag'
import { useNavigation } from '@zet/common/hooks/useNavigation'
import { MY_TEAMS_ADVISOR } from '@zet/constants'
import { BottomSheet, colors as zetColors } from '@zet/design-system'
import Avatar from '@zet/design-system/avatar/Avatar'
import { Button } from '@zet/design-system/button'
import { Icon } from '@zet/design-system/icons'
import { Stack, XStack, YStack } from '@zet/design-system/layout'
import Pressable from '@zet/design-system/pressable'
import { B2, B5, BTN, SH1, SH3 } from '@zet/design-system/typography'
import { APP_NAME } from '@zet/end-points'
import { useExperiments } from '@zet/experiments'
import { useBrandDetailsById } from '@zet/feature-brands/api/brands/hooks'
import SbmCrossSell from '@zet/feature-brands/components/sbmCrossSell'
import CbmCrossSell from '@zet/feature-brands/screens/brandListV2/cbmCrossSell'
import { useLeadDetails } from '@zet/feature-leads/api/leadDetails/hooks'
import HdfcArn from '@zet/feature-leads/components/hdfcArn'
import RaiseDisputeFloatingButton from '@zet/feature-leads/components/raiseDisputeFloatingButton'
import ReportAlert from '@zet/feature-leads/components/reportAlert'
import useRaiseDisputeNavigation from '@zet/feature-leads/utils/hooks/useRaiseDisputeNavigation'
import { useUserContext } from '@zet/feature-user'
import { i18n } from '@zet/localization'
import { DEVICE_WIDTH, HDFC_ASKCODES, LeadStatus } from '@zet/utils/Constants'
import { LinearGradient } from 'expo-linear-gradient'
import _ from 'lodash'
import Animated, {
  Extrapolation,
  interpolate,
  useAnimatedScrollHandler,
  useAnimatedStyle,
  useSharedValue,
} from 'react-native-reanimated'

import ReferAndEarnBanner from '../../components/referAndEarnBanner'
import HelpCTA from './helpCTA'

const HEADER_EXPANDED_HEIGHT = 75
const HEADER_COLLAPSED_HEIGHT = 50

// TODO: Verify ask and remove comments from 47-53
const actionRequiredAsk = (asks) => {
  if (asks?.length > 0) {
    return asks[asks.length - 1]
  }
  // if (asks.length === 1) {

  //   return _.map(asks, (ocAsk) => ocAsk)
  // } else {
  //   const arAsks = [asks[asks.length - 2], asks[asks.length - 1]]
  //   return _.map(arAsks, (ocAsk) => ocAsk)
  // }
}

const LeadProgress = ({ route }) => {
  const { data } = useExperiments()
  const hideBottomView = route?.params?.hideBottomView
  const [isBottomSheetVisible, setIsBottomSheetVisible] = useState(false)
  const [openBottomSheet, setOpenBottomSheet] = useState(false)
  const [brandLines, setBrandLines] = useState(2)
  const { navigate } = useNavigation()
  const leadId = route?.params?.leadId
  const { data: item, isLoading, refetch, isSuccess } = useLeadDetails(leadId)
  const brandId = item?.partnerId
  const { data: brandComms } = useBrandDetailsById(brandId || '')
  const asks = _.orderBy(item?.applicationAsks, ['isResolved'], ['desc'])

  const ask = actionRequiredAsk(asks)

  const offset = useSharedValue(0)

  const { data: user } = useUserContext()
  const bonusLead = item?.isBonusLead
  const subAgentName = item?.subAgentName
  const expiryDays = item?.expiryDays

  const { enabled = true, visible = true } = item?.raiseComplaint || {}

  const brandName = brandComms?.name || ''

  const { raiseDisputeNavigation, isRaiseDisputeAllowed } =
    useRaiseDisputeNavigation({
      item,
      user,
      brandName,
    })

  const scrollHandler = useAnimatedScrollHandler((event) => {
    offset.value = event.contentOffset.y
  })

  const headerAnimatedStyle = useAnimatedStyle(() => {
    return {
      height: interpolate(
        offset.value,
        [0, HEADER_EXPANDED_HEIGHT],
        [HEADER_EXPANDED_HEIGHT, HEADER_COLLAPSED_HEIGHT],
        Extrapolation.CLAMP
      ),
    }
  })

  const expiryTextAnimatedStyle = useAnimatedStyle(() => {
    return {
      opacity: interpolate(
        offset.value,
        [0, HEADER_EXPANDED_HEIGHT],
        [1, 0],
        Extrapolation.CLAMP
      ),
    }
  })

  useEffect(() => {
    const updateNumberOfLines = () => {
      const newLines = Math.round(
        interpolate(
          offset.value,
          [0, HEADER_EXPANDED_HEIGHT],
          [2, 1],
          Extrapolation.CLAMP
        )
      )
      setBrandLines(newLines)
    }

    const interval = setInterval(updateNumberOfLines, 50) // Check every 50ms for smooth updates
    return () => clearInterval(interval)
  }, [offset])

  useEffect(() => {
    if (hideBottomView) {
      publishEvent('screen_view', {
        screen_name: 'my_teams_leads_page',
      })
    }
  }, [hideBottomView])

  return (
    <Stack className="mb-2 flex-1">
      <LinearGradient
        colors={[zetColors.tertiary[100], zetColors.tertiary[100]]}
        start={[0, 0]}
        end={[1, 0.0]}
        locations={[0.0, 1.0]}
      >
        <TransparentHeader
          showBackIcon
          headerRightButton={
            data?.UI_isRaiseComplaint || hideBottomView ? (
              <></>
            ) : (
              <Button
                onPress={() => navigate('LeadsHelp', { item, brandName })}
                iconLeft={
                  <Icon
                    color={zetColors.secondary[50]}
                    type="ChatSupportFilled"
                  />
                }
                className="items-center justify-center px-0"
                textStyle="text-secondary-50 ml-1"
                textOnly={true}
                eventName="click_action"
                eventPayload={{
                  item_name: 'lead_page_help_and_support',
                  lead_id: leadId,
                  lead_status: item?.status,
                  user_id: user?.id,
                  customer_id: item?.customerId,
                  partner_id: brandId,
                  brand_name: brandName,
                  brand_category: brandComms?.subCategory,
                }}
              >
                {i18n.t('leads.help&Support')}
              </Button>
            )
          }
        />
        {bonusLead ? (
          <Pressable
            onPress={() => setIsBottomSheetVisible(true)}
            eventName="click_action"
            eventPayload={{
              item_name: 'lead_page_bonus_lead',
              lead_id: leadId,
              lead_status: item?.status,
              user_id: user?.id,
              customer_id: item?.customerId,
              partner_id: brandId,
              brand_name: brandName,
              brand_category: brandComms?.subCategory,
            }}
          >
            <Tag
              infoIcon
              colors={[zetColors.tag.shade1, zetColors.tag.shade2]}
              className="mb-2 flex px-[20] py-2"
              icon={require('../../assets/images/gift.png')}
              title={i18n.t('myLeads.bonus')}
            />
          </Pressable>
        ) : null}
        {APP_NAME !== MY_TEAMS_ADVISOR && subAgentName && (
          <Stack className="bg-lead-subAgentLead px-4/2 mb-2 items-start py-2">
            <B5 className=" text-grays-40">
              {i18n.t('myLeads.addedBy', {
                subAgentName: subAgentName,
              })}
            </B5>
          </Stack>
        )}
        <XStack className="mx-3 mb-3 items-center justify-between">
          <XStack>
            <Avatar name={item?.name || ''} size="large" />
            <Stack className="ml-4/2 justify-start ">
              {item?.name ? (
                <SH3 className=" text-neutral-white100">{item?.name}</SH3>
              ) : null}
              {item?.phoneNumber ? (
                <B5 className="text-grays-40">+91-{item?.phoneNumber}</B5>
              ) : null}
            </Stack>
          </XStack>
          <Stack className="border-grays-50 rounded border p-1">
            <Pressable
              onPress={() => Linking.openURL(`tel:${item?.phoneNumber}`)}
              eventName="click_action"
              eventPayload={{
                item_name: hideBottomView
                  ? 'my_teams_leads_page'
                  : 'lead_page_dialer_button',
                lead_id: leadId,
                lead_status: item?.status,
                user_id: user?.id,
                customer_id: item?.customerId,
                partner_id: brandId,
                brand_name: brandName,
                brand_category: brandComms?.subCategory,
              }}
            >
              <Icon
                type="PhoneFilled"
                size={24}
                color={zetColors.neutral['white100']}
              />
            </Pressable>
          </Stack>
        </XStack>
        <Animated.View style={[headerAnimatedStyle]}>
          <XStack className="border-grays-60 justify-between border-t p-3">
            <Animated.Text
              style={{
                width: DEVICE_WIDTH * 0.6,
              }}
              numberOfLines={brandLines}
              ellipsizeMode="tail"
              className="flex-1"
            >
              <B2 className="text-grays-10">{brandName}</B2>
            </Animated.Text>
            <YStack className="w-1/2 items-end">
              {item?.id ? (
                <B2 className="text-grays-40">
                  {i18n.t('leads.leadId', { leadId: item?.id })}
                </B2>
              ) : null}
              <Animated.Text
                style={[{ paddingTop: 2 }, expiryTextAnimatedStyle]}
              >
                {item?.status === LeadStatus.EXPIRED ? (
                  <B5 className="text-error-50">{i18n.t('leads.expired')}</B5>
                ) : expiryDays &&
                  (item?.status === LeadStatus.LEAD_ADDED ||
                    item?.status === LeadStatus.ACTION_REQUIRED) ? (
                  <B5 className="text-grays-50">
                    {expiryDays > 1
                      ? i18n.t('leads.expiryDays', { expiryDays })
                      : expiryDays === 1
                      ? i18n.t('leads.expireToday')
                      : i18n.t('leads.expired')}
                  </B5>
                ) : null}
              </Animated.Text>
            </YStack>
          </XStack>
        </Animated.View>
      </LinearGradient>
      <Animated.ScrollView
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl refreshing={isLoading} onRefresh={refetch} />
        }
        onScroll={scrollHandler}
        scrollEventThrottle={16}
      >
        <ReportAlert
          item={item}
          brandName={brandName}
          brandComms={brandComms}
          askCode={ask?.askCode}
        />
        {ask?.askCode === HDFC_ASKCODES.$ARN_REQUIRED ||
        ask?.askCode === HDFC_ASKCODES.$ARN_PENDING ||
        ask?.askCode === HDFC_ASKCODES.$ARN_INCORRECT ? (
          <>
            <HdfcArn item={item} brandComms={brandComms} ask={ask} />
            <ReferAndEarnBanner containerClassName="mb-2" />
          </>
        ) : (
          <>
            <StatusCard
              item={item}
              brandComms={brandComms}
              brandName={brandName}
              hideBottomView={hideBottomView}
              setOpenBottomSheet={setOpenBottomSheet}
            />
            {item?.status === 'rejected' ? (
              <>
                <SbmCrossSell sectionTitle={'Chance to Earn ₹1500'} />
                <CbmCrossSell sectionTitle="Chance to Earn ₹300" />
                <Footer marginTop={false} />
              </>
            ) : (
              <ReferAndEarnBanner containerClassName="mb-2" />
            )}
          </>
        )}
        {isSuccess && visible && <HelpCTA leadId={leadId} enabled={enabled} />}
      </Animated.ScrollView>

      {data?.UI_isRaiseComplaint ||
        (Boolean(isRaiseDisputeAllowed) && MY_TEAMS_ADVISOR !== 'my-teams' && (
          <RaiseDisputeFloatingButton
            item={item}
            user={user}
            raiseDisputeNavigation={raiseDisputeNavigation}
            brandId={brandId}
            brandName={brandName}
          />
        ))}
      {openBottomSheet && (
        <TotalTransactionBottomSheet
          setOpenBottomSheet={setOpenBottomSheet}
          openBottomSheet={openBottomSheet}
          totalCommission={item?.totalCommission}
          item={item}
        />
      )}
      <BottomSheet
        visible={isBottomSheetVisible}
        disableBottomSheet={() => setIsBottomSheetVisible(false)}
        onDismiss={() => setIsBottomSheetVisible(false)}
        sheetName="bonus_lead"
      >
        <XStack className="items-center px-2 py-3">
          <Image
            source={require('../../assets/images/gift.png')}
            className="mr-2 h-[20] w-[20]"
          />
          <SH1>{i18n.t('bonusLead.bonus')}</SH1>
        </XStack>
        <YStack className="border-grays-30 items-start justify-center border-b border-t p-3">
          <B2>{i18n.t('bonusLead.bonusLeadDescription1')}</B2>
          <B2 className="mt-3">{i18n.t('bonusLead.bonusLeadDescription2')}</B2>
        </YStack>
        <Stack className="px-4/2 items-center py-3">
          <Button
            fullWidth
            size={'md'}
            className={'bg-primary-100 justify-center px-3 py-2'}
            onPress={() => setIsBottomSheetVisible(false)}
            eventName="click_action"
            eventPayload={{
              item_name: 'lead_page_bonus_lead_got_it',
              lead_id: leadId,
              lead_status: item?.status,
              user_id: user?.id,
              customer_id: item?.customerId,
              partner_id: brandId,
              brand_name: brandName,
              brand_category: brandComms?.subCategory,
            }}
          >
            <BTN className="text-neutral-white100 text-center">
              {i18n.t('bonusLead.gotIt')}
            </BTN>
          </Button>
        </Stack>
      </BottomSheet>
    </Stack>
  )
}

export default LeadProgress
